"use client";

import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

const PALETTE = [
  "#1a2b5f",
  "#2c4a8c",
  "#5BA4E6",
  "#2d8a5f",
  "#7a9bc7",
  "#0f1a3d",
  "#1f3b73",
  "#6aa0d6",
  "#355f88",
];

const formatDateLabel = (date, range) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  if (range === "1M" || range === "3M") {
    return `${month} ${day}`;
  }
  return `${month} ${year}`;
};

const getRangeStart = (range, endDate) => {
  const startDate = new Date(endDate);
  switch (range) {
    case "1M":
      startDate.setMonth(endDate.getMonth() - 1);
      break;
    case "3M":
      startDate.setMonth(endDate.getMonth() - 3);
      break;
    case "1Y":
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    case "ALL":
      startDate.setFullYear(endDate.getFullYear() - 20);
      break;
    default:
      break;
  }
  return startDate;
};

const filterSeriesByRange = (labels, datasets, range, transformFn) => {
  const endDate = new Date();
  const startDate = getRangeStart(range, endDate);
  const filteredLabels = [];
  const filteredDatasets = datasets.map(() => []);

  for (let i = 0; i < labels.length; i++) {
    const labelDate = new Date(labels[i]);
    if (Number.isNaN(labelDate.getTime())) {
      continue;
    }
    if (labelDate >= startDate && labelDate <= endDate) {
      filteredLabels.push(formatDateLabel(labelDate, range));
      datasets.forEach((dataset, datasetIndex) => {
        const rawValue = dataset?.[i];
        const value = transformFn ? transformFn(rawValue, datasetIndex) : rawValue;
        filteredDatasets[datasetIndex].push(value);
      });
    }
  }

  return {
    labels: filteredLabels,
    datasets: filteredDatasets,
  };
};

const CHART_COLORS = {
  text: "#5a6577",
  grid: "rgba(26, 43, 95, 0.1)",
  legend: "#1a2b5f",
};

const MAX_CANVAS_RETRIES = 60;
const DELAYED_RETRY_MS = [100, 250, 500, 750];

function scheduleChartInit(onRun, getCancelRef) {
  let rafId = null;
  let timeoutIds = [];

  function tryRun() {
    if (getCancelRef?.()?.current) return false;
    return onRun();
  }

  function runRafLoop(attempt) {
    if (getCancelRef?.()?.current) return;
    if (tryRun()) return;
    if (attempt < MAX_CANVAS_RETRIES) {
      rafId = requestAnimationFrame(() => runRafLoop(attempt + 1));
    } else {
      DELAYED_RETRY_MS.forEach((ms) => {
        const id = setTimeout(() => {
          if (getCancelRef?.()?.current) return;
          tryRun();
        }, ms);
        timeoutIds.push(id);
      });
    }
  }

  if (!tryRun()) {
    rafId = requestAnimationFrame(() => runRafLoop(0));
  }

  return () => {
    if (rafId != null) cancelAnimationFrame(rafId);
    timeoutIds.forEach((id) => clearTimeout(id));
  };
}

export default function DashboardCharts({
  portfolioAllocation,
  growthSeries,
  growthRange = "Total",
  assetsSeries,
  apySeries,
  assetsRange,
  apyRange,
}) {
  const portfolioChartRef = useRef(null);
  const growthChartRef = useRef(null);
  const assetsChartRef = useRef(null);
  const apyChartRef = useRef(null);
  const portfolioCancelledRef = useRef(false);
  const growthCancelledRef = useRef(false);
  const assetsCancelledRef = useRef(false);
  const apyCancelledRef = useRef(false);

  useEffect(() => {
    Chart.register(ChartDataLabels);
    if (!Chart.defaults.plugins) {
      Chart.defaults.plugins = {};
    }
    Chart.defaults.plugins.datalabels = { display: false };
  }, []);

  useEffect(() => {
    portfolioCancelledRef.current = false;
    const allocationLabels = portfolioAllocation?.labels || [];
    const allocationValues = portfolioAllocation?.values || [];
    const allocationColors = allocationLabels.map(
      (_, index) => PALETTE[index % PALETTE.length]
    );

    const cancel = scheduleChartInit(
      () => {
        const portfolioCanvas = document.getElementById("portfolioChart");
        if (!portfolioCanvas) return false;
        const portfolioCtx = portfolioCanvas.getContext("2d");
        if (!portfolioCtx) return false;

        if (portfolioChartRef.current) {
          const chart = portfolioChartRef.current;
          chart.data.labels = allocationLabels;
          if (chart.data.datasets[0]) {
            chart.data.datasets[0].data = allocationValues;
            chart.data.datasets[0].backgroundColor = allocationColors;
          }
          chart.update("none");
          return true;
        }

        portfolioChartRef.current = new Chart(portfolioCtx, {
          type: "doughnut",
          data: {
            labels: allocationLabels,
            datasets: [
              {
                data: allocationValues,
                backgroundColor: allocationColors,
                borderWidth: 0,
                cutout: "70%",
              },
            ],
          },
          plugins: [ChartDataLabels],
          options: {
            responsive: true,
            maintainAspectRatio: true,
            cutout: "70%",
            plugins: {
              legend: {
                position: "right",
                labels: {
                  color: CHART_COLORS.legend,
                  padding: 12,
                  font: {
                    family: "'Source Serif 4', serif",
                    size: 12,
                  },
                },
              },
              datalabels: {
                display: true,
                color: "#FFFFFF",
                font: {
                  family: "'Source Serif 4', serif",
                  weight: "bold",
                  size: 12,
                },
                formatter: (value) => {
                  const numeric = Number(value);
                  if (!Number.isFinite(numeric) || numeric <= 0) {
                    return "";
                  }
                  return `${numeric.toFixed(2)}%`;
                },
                anchor: "center",
                align: "center",
              },
            },
          },
        });
        return true;
      },
      () => portfolioCancelledRef
    );

    return () => {
      portfolioCancelledRef.current = true;
      cancel();
    };
  }, [portfolioAllocation]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    growthCancelledRef.current = false;

    const chartRange = growthRange === "Total" ? "ALL" : growthRange;
    const growthData = filterSeriesByRange(
      growthSeries?.labels || [],
      [growthSeries?.values || []],
      chartRange,
      (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
      }
    );
    const growthLabels = growthData.labels;
    const growthValues = growthData.datasets[0] || [];
    const growthBaseline =
      growthValues.find((v) => Number.isFinite(Number(v))) ?? null;

    const cancel = scheduleChartInit(
      () => {
        const growthCanvas = document.getElementById("growthChart");
        if (!growthCanvas) return false;
        const growthCtx = growthCanvas.getContext("2d");
        if (!growthCtx) return false;

        if (growthChartRef.current) {
          const chart = growthChartRef.current;
          chart.data.labels = growthLabels;
          if (chart.data.datasets[0]) {
            chart.data.datasets[0].data = growthValues;
          }
          chart.$growthBaseline = growthBaseline;
          chart.$growthHoverIndex = null;
          chart.update("none");
          return true;
        }

        const growthLineHoverPlugin = {
      id: "growthLineHoverPlugin",
      afterEvent: (chart, args) => {
        const event = args?.event;
        if (!event) {
          return;
        }

        if (event.type === "mouseout") {
          chart.setActiveElements([]);
          chart.tooltip.setActiveElements([], { x: 0, y: 0 });
          chart.update("none");
          chart.$growthHoverIndex = null;
          return;
        }

        if (event.type !== "mousemove" && event.type !== "touchmove") {
          return;
        }

        const { chartArea, scales } = chart;
        if (!chartArea || !scales?.x) {
          return;
        }

        const yScale = scales.y;
        const leftBoundary = yScale ? yScale.left : chartArea.left;
        const within =
          event.x >= leftBoundary &&
          event.x <= chartArea.right &&
          event.y >= chartArea.top &&
          event.y <= chartArea.bottom;

        if (!within) {
          chart.setActiveElements([]);
          chart.tooltip.setActiveElements([], { x: 0, y: 0 });
          chart.update("none");
          chart.$growthHoverIndex = null;
          return;
        }

        const rawIndex = scales.x.getValueForPixel(event.x);
        let index = Number.isFinite(rawIndex)
          ? Math.round(rawIndex)
          : scales.x.getLabels().indexOf(rawIndex);
        const data = chart.data?.datasets?.[0]?.data || [];
        if (!data.length) {
          return;
        }

        if (index < 0) {
          index = 0;
        }
        if (index >= data.length) {
          index = data.length - 1;
        }

        if (!Number.isFinite(Number(data[index]))) {
          let left = index - 1;
          let right = index + 1;
          let found = -1;
          while (left >= 0 || right < data.length) {
            if (left >= 0 && Number.isFinite(Number(data[left]))) {
              found = left;
              break;
            }
            if (right < data.length && Number.isFinite(Number(data[right]))) {
              found = right;
              break;
            }
            left -= 1;
            right += 1;
          }
          if (found === -1) {
            return;
          }
          index = found;
        }

        if (chart.$growthHoverIndex === index) {
          return;
        }

        const meta = chart.getDatasetMeta(0);
        const point = meta?.data?.[index];
        if (!point) {
          return;
        }

        chart.$growthHoverIndex = index;
        chart.setActiveElements([{ datasetIndex: 0, index }]);
        chart.tooltip.setActiveElements([{ datasetIndex: 0, index }], {
          x: event.x,
          y: point.y,
        });
        chart.update("none");
      },
    };

    growthChartRef.current = new Chart(growthCtx, {
      type: "line",
      data: {
        labels: growthLabels,
        datasets: [
          {
            label: "Growth of $1",
            data: growthValues,
            borderColor: "#5BA4E6",
            backgroundColor: "rgba(91, 164, 230, 0.1)",
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHitRadius: 16,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: "#5BA4E6",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          axis: "x",
          intersect: false,
        },
        hover: {
          mode: "index",
          axis: "x",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1a2b5f",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            displayColors: false,
            padding: 10,
            mode: "index",
            intersect: false,
            titleFont: {
              family: "'Source Serif 4', serif",
              size: 12,
              weight: "600",
            },
            bodyFont: {
              family: "'Source Serif 4', serif",
              size: 11,
              weight: "400",
            },
            titleMarginBottom: 8,
            callbacks: {
              title: (items) => items?.[0]?.label || "",
              label: (context) => {
                const value = Number(context.parsed?.y);
                if (!Number.isFinite(value)) {
                  return [];
                }
                const chartInstance = context.chart;
                const baselineRaw = chartInstance?.$growthBaseline;
                const baseline = Number(baselineRaw);
                const hasBaseline =
                  Number.isFinite(baseline) && baseline !== 0;
                const returnValue = hasBaseline
                  ? ((value / baseline) - 1) * 100
                  : (value - 1) * 100;
                const sign = returnValue >= 0 ? "+" : "";
                return [
                  `Value: ${value.toFixed(2)}`,
                  `Return: ${sign}${returnValue.toFixed(2)}%`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: CHART_COLORS.grid },
            ticks: { color: CHART_COLORS.text, font: { size: 10 } },
          },
          y: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => Number(value).toFixed(2),
            },
          },
        },
      },
      plugins: [growthLineHoverPlugin],
    });
        return true;
      },
      () => growthCancelledRef
    );

    return () => {
      growthCancelledRef.current = true;
      cancel();
    };
  }, [growthSeries, growthRange]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    assetsCancelledRef.current = false;

    const assetsData = filterSeriesByRange(
      assetsSeries?.labels || [],
      [assetsSeries?.totalAssets || [], assetsSeries?.netExposure || []],
      assetsRange,
      (value) => Number(value)
    );

    // Calculate the difference: Total Assets - Net Exposure (for the top layer)
    const totalAssets = assetsData.datasets[0] || [];
    const netExposure = assetsData.datasets[1] || [];
    const difference = totalAssets.map((total, index) => {
      const totalNum = Number(total) || 0;
      const netNum = Number(netExposure[index]) || 0;
      return totalNum - netNum;
    });

    const cancel = scheduleChartInit(
      () => {
        const assetsCanvas = document.getElementById("assetsChart");
        if (!assetsCanvas) return false;
        const assetsCtx = assetsCanvas.getContext("2d");
        if (!assetsCtx) return false;

        if (assetsChartRef.current) {
          const chart = assetsChartRef.current;
          chart.data.labels = assetsData.labels;
          if (chart.data.datasets[0]) {
            chart.data.datasets[0].data = difference;
          }
          if (chart.data.datasets[1]) {
            chart.data.datasets[1].data = netExposure;
          }
          chart.update("none");
          return true;
        }

        assetsChartRef.current = new Chart(assetsCtx, {
      type: "bar",
      data: {
        labels: assetsData.labels,
        datasets: [
          {
            label: "Total Assets Value - Net Exposure Value",
            data: difference,
            backgroundColor: "#5BA4E6",
            borderRadius: 4,
          },
          {
            label: "Net Exposure Value",
            data: netExposure,
            backgroundColor: "#1a2b5f",
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: CHART_COLORS.text, font: { size: 10 } },
          },
          y: {
            stacked: true,
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) {
                  return value;
                }
                if (window.innerWidth < 768) {
                  const absValue = Math.abs(numeric);
                  if (absValue >= 1e6) {
                    return `${(numeric / 1e6).toFixed(1)}M`;
                  }
                  if (absValue >= 1e3) {
                    return `${(numeric / 1e3).toFixed(1)}K`;
                  }
                }
                return numeric.toLocaleString("en-US");
              },
            },
          },
        },
      },
    });
        return true;
      },
      () => assetsCancelledRef
    );

    return () => {
      assetsCancelledRef.current = true;
      cancel();
    };
  }, [assetsSeries, assetsRange]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    apyCancelledRef.current = false;

    const apyData = filterSeriesByRange(
      apySeries?.labels || [],
      [apySeries?.expected || [], apySeries?.ninetyDay || [], apySeries?.thirtyDay || []],
      apyRange,
      (value, datasetIndex) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) {
          return null;
        }
        if (datasetIndex !== 0 && numeric === 0) {
          return null;
        }
        return numeric * 100;
      }
    );

    const cancel = scheduleChartInit(
      () => {
        const apyCanvas = document.getElementById("apyChart");
        if (!apyCanvas) return false;
        const apyCtx = apyCanvas.getContext("2d");
        if (!apyCtx) return false;

        if (apyChartRef.current) {
          const chart = apyChartRef.current;
          chart.data.labels = apyData.labels;
          if (chart.data.datasets[0]) {
            chart.data.datasets[0].data = apyData.datasets[0] || [];
          }
          if (chart.data.datasets[1]) {
            chart.data.datasets[1].data = apyData.datasets[1] || [];
          }
          if (chart.data.datasets[2]) {
            chart.data.datasets[2].data = apyData.datasets[2] || [];
          }
          chart.update("none");
          return true;
        }

        apyChartRef.current = new Chart(apyCtx, {
      type: "line",
      data: {
        labels: apyData.labels,
        datasets: [
          {
            label: "Expected APY",
            data: apyData.datasets[0] || [],
            borderColor: "#5BA4E6",
            backgroundColor: "rgba(91, 164, 230, 0.2)",
            fill: true,
            tension: 0.4,
            pointRadius: 0,
          },
          {
            label: "90-Day APY",
            data: apyData.datasets[1] || [],
            borderColor: "#2D8A5F",
            backgroundColor: "transparent",
            fill: false,
            tension: 0.4,
            pointRadius: 0,
          },
          {
            label: "30-Day APY",
            data: apyData.datasets[2] || [],
            borderColor: "#1a2b5f",
            backgroundColor: "transparent",
            fill: false,
            tension: 0.4,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            grid: { color: CHART_COLORS.grid },
            ticks: { color: CHART_COLORS.text, font: { size: 10 } },
          },
          y: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    });
        return true;
      },
      () => apyCancelledRef
    );

    return () => {
      apyCancelledRef.current = true;
      cancel();
    };
  }, [apySeries, apyRange]);

  useEffect(() => {
    return () => {
      if (portfolioChartRef.current) {
        portfolioChartRef.current.destroy();
        portfolioChartRef.current = null;
      }
      if (growthChartRef.current) {
        growthChartRef.current.destroy();
        growthChartRef.current = null;
      }
      if (assetsChartRef.current) {
        assetsChartRef.current.destroy();
        assetsChartRef.current = null;
      }
      if (apyChartRef.current) {
        apyChartRef.current.destroy();
        apyChartRef.current = null;
      }
    };
  }, []);

  return null;
}
